import React, { useState, useEffect, useRef } from "react";
import "./Reports.css";
import { Pagination } from "@material-ui/lab";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
// xlsx-js-style is a drop-in fork of "xlsx" that adds support for writing
// cell styles (bold, alignment, etc). The plain "xlsx" community package
// silently ignores style info on write, so we need this fork for the
// bold/centered header row below. Install with: npm install xlsx-js-style
import { utils, writeFile } from "xlsx-js-style";
import { hasGlobalStations, canFilterStations, scopeStations, allowedSerials } from '../components/roleAccess';
import traIcon from '../media/icons/logo.png';
import { API_BASE } from '../config';
import { tr } from '../i18n';
import ScrollX from '../components/ScrollX/ScrollX';

//Styles for filter input fields.
const useStyles = makeStyles((theme) => ({
  formControl: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  FilterClearButtonClass: {
    float: 'right',
    margin: 10,
  },
}));

// The empty state, kept in one place because it is needed twice: once as the
// initial state and once by "Clear Fields". They used to be two separate
// object literals, so every new filter had to be added to both — and a filter
// missed in the second one would look cleared while still being applied.
const emptyFilterState = () => ({
  current_page: 1,
  station_serials: "",
  pump_names: "",
  nozzle_names: "",
  since_date: "",
  to_date: "",
  // Query-parameter name, not a column name. The API still accepts
  // `car_plates` and applies it to the license_plate column, so this key
  // stays as-is; renaming it here would only break the URL contract.
  car_plates: "",
  product_names: "",
  tank_names: "",
  card_ids: "",
  tag_ids: "",
  // Exact-match numeric filters. Held as strings because they come straight
  // from text inputs — "" means "not filtering", which is NOT the same as 0
  // and must never be sent as one.
  volumes: "",
  amounts: "",
});

// Sent only when filled in and numeric; see buildFilterQuery below. Plural
// names to match the existing `card_ids` / `tag_ids` convention, and because
// the API reads them with getlist() — passing the same key twice filters on
// either value.
const EXACT_NUMERIC_PARAMS = ['volumes', 'amounts'];

// ONE query-string builder, used by both the table fetch and the Excel
// export.
//
// These were previously two separate copies of the same twenty-odd lines. That
// is the classic way a filter ends up applying to what you see on screen but
// not to what you download — the export quietly returns more rows than the
// table showed, and nothing warns you. Any filter added here now reaches both
// paths by construction.
const buildFilterQuery = (state) => {
  let q =
    state.station_serials +
    state.pump_names +
    state.nozzle_names +
    state.product_names +
    state.tank_names;

  if (state.since_date !== "") {
    q += `since_date=${state.since_date.replace(/T/, " ")}&`;
  }
  if (state.to_date !== "") {
    q += `to_date=${state.to_date.replace(/T/, " ")}&`;
  }
  // Encoded, unlike the original: a Tanzanian plate is typed with spaces
  // ("T 324 DLS"), and a raw space in a query string is not valid.
  if (state.car_plates !== "") {
    q += `car_plates=${encodeURIComponent(state.car_plates)}&`;
  }
  if (state.card_ids !== "") {
    q += `card_ids=${encodeURIComponent(state.card_ids)}&`;
  }
  if (state.tag_ids !== "") {
    q += `tag_ids=${encodeURIComponent(state.tag_ids)}&`;
  }

  // A blank box means "not filtering" and is left out entirely. Anything that
  // is not a number is also left out rather than sent as-is — a stray
  // character would otherwise reach the server as `volumes=1o` and either 500
  // or, worse, be silently coerced into something nobody typed.
  //
  // Number() also normalizes the text: "10.00" and "10" both become 10, so
  // the value sent matches regardless of how it was typed.
  EXACT_NUMERIC_PARAMS.forEach((name) => {
    const raw = String(state[name] ?? '').trim();
    if (raw !== '' && !Number.isNaN(Number(raw))) {
      q += `${name}=${Number(raw)}&`;
    }
  });

  return q;
};

// TRA verification link -> the receipt verification code, for the Excel export:
//   https://verify.tra.go.tz/24FE5C506781_154538  ->  24FE5C506781
// (the last part of the address, before the "_" and the time). Empty when the
// sale has no link.
const verificationCodeOf = (link) => {
  if (!link) return '';
  const path = String(link).trim().split(/[?#]/)[0].replace(/\/+$/, '');
  return path.slice(path.lastIndexOf('/') + 1).split('_')[0];
};

//Reports component includes filter input fields.
export function Reports({toggleButton, checkboxesView, token}) {
  //Filter input fields state.
  const classes = useStyles();
  const [state, setState] = useState(emptyFilterState);
  const [reportData, setReportData] = useState([]);
  const [currentPageState, setCurrentPageState] = useState({});
  const [loading, setLoading] = useState(false);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [stationsArray, setStationsArray] = useState([]);
  const [pumpsArray, setPumpsArray] = useState([]);
  const [nozzleArrayData, setNozzleArrayData] = useState([]);
  const [filterTankArrayData, setFilterTankArrayData] = useState([]);
  const [filterProductData, setFilterProductData] = useState([]);
  const [checkedNozzle, setCheckedNozzle] = useState(false);
  const [checkedFuel, setCheckedFuel] = useState(false);
  const [checkedTank, setCheckedTank] = useState(false);
  const [checkedAmount, setCheckedAmount] = useState(true);
  const [checkedPrice, setCheckedPrice] = useState(false);
  // Car Plate and Customer Name are shown by default now. This column used to
  // be labelled "TIN Number" and read item.car_plate, back when the station
  // sent only a TIN and the server parked it in its plate column. The two are
  // separate fields end to end now: item.license_plate is the vehicle's plate,
  // item.tin_number is the customer's tax id, which has its own column below.
  const [checkedPlate, setCheckedPlate] = useState(true);
  // Shown by default, same as Car Plate and Customer Name. The station sends
  // license_plate and tin_number as separate fields now, so there is a real
  // TIN to show rather than the plate wearing a TIN label.
  const [checkedTin, setCheckedTin] = useState(true);
  const [checkedCustomerName, setCheckedCustomerName] = useState(true);
  const [checkedSalesId, setCheckedSalesId] = useState(false);
  const [checkedReceiptId, setCheckedReceiptId] = useState(false);
  const [checkedStation, setCheckedStation] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const isMountedRef = useRef(true);
  // Silent cache of already-fetched pages, keyed by "filters|pageNumber".
  // Populated by a background prefetch after each page loads (see the
  // main fetch effect below) - not shown anywhere in the UI, just used
  // to skip a network round-trip when the user clicks to a page that's
  // already been quietly fetched ahead of time.
  const pageCacheRef = useRef({});

  // serial -> station name, for the Excel export.
  //
  // /sales_reports/all returns station_serial_number, not a name, so the
  // export has to resolve it locally. stationsArray can't be used for this:
  // it only keeps { title, query } (no serial paired to the name) and is only
  // fetched while the filter panel is open, so an export done with the panel
  // closed would have nothing to look up.
  const stationNameBySerialRef = useRef({});

  const handleCheckboxChangeNozzle = (event) => {
    setCheckedNozzle(event.target.checked);
  };

  const handleCheckboxChangeFuel = (event) => {
    setCheckedFuel(event.target.checked);
  };

  const handleCheckboxChangeTank = (event) => {
    setCheckedTank(event.target.checked);
  };

  const handleCheckboxChangeAmount = (event) => {
    setCheckedAmount(event.target.checked);
  };

  const handleCheckboxChangePrice = (event) => {
    setCheckedPrice(event.target.checked);
  };

  const handleCheckboxChangePlate = (event) => {
    setCheckedPlate(event.target.checked);
  };

  const handleCheckboxChangeTin = (event) => {
    setCheckedTin(event.target.checked);
  };

  const handleCheckboxChangeCustomerName = (event) => {
    setCheckedCustomerName(event.target.checked);
  };

  const handleCheckboxChangeSalesId = (event) => {
    setCheckedSalesId(event.target.checked);
  };

  const handleCheckboxChangeReceiptId = (event) => {
    setCheckedReceiptId(event.target.checked);
  };

  const handleCheckboxChangeStation = (event) => {
    setCheckedStation(event.target.checked);
  };

  let itemNo = 0;

  //Handle change for filter input fields.
  const handleChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    // Append seconds ":00" if not provided (assuming time format: "hh:mm" or "hh:mm:ss")
    if (e.target.type === "datetime-local" && value.length === 16) {
      value += ":00";
    }

    setState((prevState) => ({ ...prevState, [name]: value }));
    initializePagination();
  };

  const handleStationsChange = (event, value) => {
    let tempQuery = "";
    if(value[0]?.query){
      value.map(item => tempQuery = tempQuery + item.query);
    }
    setState((prevState) => ({ ...prevState, station_serials: tempQuery }));
    initializePagination();
  };

  const handlePumpChange = (event, value) => {
    let tempQuery = "";
    if(value[0]?.query){
      value.map(item => tempQuery = tempQuery + item.query);
    }
    setState((prevState) => ({ ...prevState, pump_names: tempQuery }));
    initializePagination();
  };

  const handleNozzleChange = (event, value) => {
    let tempQuery = "";
    if(value[0]?.query){
      value.map(item => tempQuery = tempQuery + item.query);
    }
    setState((prevState) => ({ ...prevState, nozzle_names: tempQuery }));
    initializePagination();
  };

  const handleTankChange = (event, value) => {
    let tempQuery = "";
    if(value[0]?.query){
      value.map(item => tempQuery = tempQuery + item.query);
    }
    setState((prevState) => ({ ...prevState, tank_names: tempQuery }));
    initializePagination();
  };

  const handleProductChange = (event, value) => {
    let tempQuery = "";
    if(value[0]?.query){
      value.map(item => tempQuery = tempQuery + item.query);
    }
    setState((prevState) => ({ ...prevState, product_names: tempQuery }));
    initializePagination();
  };

  const handleExport = () => {
    const abortController = new AbortController();
    setIsExporting(true);

    const MAX_EXPORT_ROWS = 5000000;

    // Same builder the table uses, so the sheet always contains exactly the
    // rows that were on screen — no more, no fewer.
    const filterQueryString = buildFilterQuery(state);

    const authHeader = { 'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token };

    const fetchJson = (url) =>
      fetch(url, { headers: authHeader, signal: abortController.signal })
        .then(async (response) => {
          if (!response.ok) {
            const bodyText = await response.text().catch(() => "");
            throw new Error(`Server returned ${response.status} ${response.statusText}. ${bodyText.slice(0, 500)}`);
          }
          return response.json();
        });

    fetchJson(
      `${API_BASE}/sales_reports/all?page_number=1&per_page=1&` + filterQueryString
    )
      .then((countData) => {
        if (!isMountedRef.current) return null;

        const total = countData?.total ?? 0;
        if (total === 0) {
          alert(tr("No records found for the selected filters."));
          return null;
        }
        if (total > MAX_EXPORT_ROWS) {
          alert(
            tr("This filter matches {0} records, which is too many to export at once (limit {1}). Please narrow your date range or filters and try again.", total.toLocaleString(), MAX_EXPORT_ROWS.toLocaleString())
          );
          return null;
        }

        return fetchJson(
          `${API_BASE}/sales_reports/all?page_number=1&per_page=${total}&` + filterQueryString
        );
      })
      .then((data) => {
        if (!isMountedRef.current || !data) return;

        const records = data?.data || [];
        if (records.length === 0) {
          alert(tr("No records found for the selected filters."));
          return;
        }

        const exportColumns = [
          { key: 'receipt_id',     header: tr("Receipt ID") },
          { key: 'station_name',   header: tr("Station Name") },
          { key: 'creation_date',  header: tr("Creation Date") },
          { key: 'volume',         header: tr("Volume") },
          { key: 'price',          header: tr("Price") },
          { key: 'amount',         header: tr("Amount") },
          { key: 'pump_name',      header: tr("Pump Name") },
          { key: 'product_name',   header: tr("Product Name") },
          { key: 'license_plate',  header: tr("Car Plate") },
          // Next to Car Plate, matching the on-screen column order — the two
          // are read together and a TIN parked at the far end of the sheet is
          // easy to miss. license_plate and tin_number are separate DB
          // columns; neither is derived from the other.
          { key: 'tin_number',     header: tr("TIN Number") },
          { key: 'customer_name',  header: tr("Customer Name") },
          // Taken from the verification link (see verificationCodeOf).
          { key: 'receipt_verification_code', header: tr("RECEIPT VERIFICATION CODE") },
          { key: 'verification_link', header: tr("Verification Link") },
        ];

        const stationNames = stationNameBySerialRef.current || {};

        const exportRows = records.map((record) => {
          const row = {};
          exportColumns.forEach(({ key, header }) => {
            if (key === 'station_name') {
              // These records carry station_serial_number, not a name. Fall
              // back to the serial itself rather than an empty cell, so a
              // station missing from /station/all is still identifiable.
              const serial = record.station_serial_number;
              row[header] = stationNames[serial] || serial || '';
            } else if (key === 'receipt_verification_code') {
              row[header] = verificationCodeOf(record.verification_link);
            } else {
              row[header] = record[key];
            }
          });
          return row;
        });

        var wb = utils.book_new(),
        ws = utils.json_to_sheet(exportRows, { header: exportColumns.map((c) => c.header) });

        const headerStyle = {
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
        exportColumns.forEach((_, colIndex) => {
          const cellAddress = utils.encode_cell({ r: 0, c: colIndex });
          if (ws[cellAddress]) {
            ws[cellAddress].s = headerStyle;
          }
        });

        // Every data cell bold and centred, like the header.
        const dataStyle = {
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
        for (let r = 1; r <= exportRows.length; r++) {
          exportColumns.forEach((_, colIndex) => {
            const cellAddress = utils.encode_cell({ r, c: colIndex });
            if (ws[cellAddress]) {
              ws[cellAddress].s = dataStyle;
            }
          });
        }

        // Verification links are full URLs - at the default width of 18 they
        // render as an unreadable sliver, so that one column gets more room.
        ws['!cols'] = exportColumns.map(({ key }) => ({
          wch: key === 'verification_link' ? 60 : key === 'receipt_verification_code' ? 30 : 18,
        }));

        utils.book_append_sheet(wb, ws, "ExcelSheet");

        const currentDateTime = new Date().toISOString().replace(/[-:]/g, '').replace('T', '-').replace('Z', '');
        const filename = `ExcelExport_${currentDateTime}.xlsx`;

        writeFile(wb, filename);
      })
      .catch((error) => {
        if (isMountedRef.current && error.name !== 'AbortError') {
          alert(tr("Export failed: {0}", error.message || error));
          console.error('Export error:', error);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsExporting(false);
        }
      });
  };

  const printReceipt = async (e) => {
    try {
      const response = await fetch(`${API_BASE}/sales_reports/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
        },
        body: JSON.stringify({id: Number(e.target.value)})
      });

      const data = await response.json();
      const verificationLink = data?.data?.verification_link;

      if (verificationLink) {
        window.open(verificationLink, '_blank');
      } else {
        alert(tr("No verification link available for this sale."));
      }
    } catch (error) {
      console.error("printReceipt failed:", error);
      alert(tr("Error fetching receipt\n{0}", (error?.message || error)));
    }
  };

  //Initialize Nozzle ID when a new pump is selected.
  const initializeNozzleId = (e) => {
    setState((prevState) => ({ ...prevState, nozzle_names: "" }));
  };

  //Initialize pagination to page 1.
  const initializePagination = (e) => {
    setState((prevState) => ({ ...prevState, current_page: 1 }));
  };

  //Set current page to state
  const setCurrentPage = (event, newPage) => {
    setState((prevState) => ({ ...prevState, current_page: newPage }));
  };

  useEffect(() => {
    const abortController = new AbortController();

    let current_page = `page_number=${state.current_page}&`;

    const filterSuffix = buildFilterQuery(state);
    const cacheKeyFor = (pageNum) => `page_number=${pageNum}&${filterSuffix}`;

    const authHeaders = { 'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token };

    if (reportData.num_pages !== 0) {
      current_page = `page_number=${state.current_page}&`;
      const thisPageKey = cacheKeyFor(state.current_page);
      const cached = pageCacheRef.current[thisPageKey];

      if (cached) {
        setReportData(cached);
        setLoading(false);
      } else {
        setLoading(true);
        fetch(
          `${API_BASE}/sales_reports/page?` +
          current_page +
          filterSuffix,
          {
            headers: authHeaders,
            signal: abortController.signal,
          }
        )
          .then((response) => response.json())
          .then((data) => {
            const records = data.data.page_records;
            pageCacheRef.current[thisPageKey] = records;
            setReportData(records);
          })
          .catch((err) => {
            if (err.name !== 'AbortError') console.error("Fetch reports error:", err);
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoading(false);
            }
          });
      }

      const nextPage = state.current_page + 1;
      const nextPageKey = cacheKeyFor(nextPage);
      if (!pageCacheRef.current[nextPageKey]) {
        fetch(
          `${API_BASE}/sales_reports/page?page_number=${nextPage}&` + filterSuffix,
          { headers: authHeaders, signal: abortController.signal }
        )
          .then((response) => response.json())
          .then((data) => {
            pageCacheRef.current[nextPageKey] = data.data.page_records;
          })
          .catch(() => { /* silent - best-effort prefetch */ });
      }

      fetch(
        `${API_BASE}/sales_reports/page?` + filterSuffix,
        {
          headers: authHeaders,
          signal: abortController.signal,
        }
      )
        .then((response) => response.json())
        .then((data) => setCurrentPageState(data.data));
    }

    return () => { abortController.abort(); };

  }, [
    state.station_serials,
    state.nozzle_names,
    state.pump_names,
    state.since_date,
    state.to_date,
    state.car_plates,
    state.product_names,
    state.tank_names,
    state.current_page,
    state.card_ids,
    state.tag_ids,
    state.volumes,
    state.amounts,
    reportData.num_pages,
  ]);

  useEffect(() => {
    if (toggleButton === "block") {
      fetch(`${API_BASE}/station/all`, {
        headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token },
      })
        .then((response) => response.json())
        .then((data) => setStationsArray(
          scopeStations(token, (data.data || []).filter(item => item.is_vfd))
            .map(item => ({ title: item.name, query: `station_serials=${item.serial_number}&` }))
        ));
    }
  }, [toggleButton, token]);

  // Populated on mount (not gated on toggleButton) so the export always has
  // names available, whether or not the user opened the filter panel.
  useEffect(() => {
    fetch(`${API_BASE}/station/all`, {
      headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token },
    })
      .then((response) => response.json())
      .then((data) => {
        const map = {};
        (data.data || []).forEach((item) => {
          if (item.serial_number) map[item.serial_number] = item.name;
        });
        stationNameBySerialRef.current = map;
      })
      .catch(() => { /* export falls back to showing the serial */ });
  }, [token]);

  useEffect(() => {
    if (hasGlobalStations(token)) return;
    const mine = allowedSerials(token) || [];
    if (mine.length) {
      const q = mine.map((s) => `station_serials=${s}&`).join('');
      setState((prev) => ({ ...prev, station_serials: q }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (toggleButton === "block") {
      fetch(`${API_BASE}/station/pumps`, {
        headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token },
      })
        .then((response) => response.json())
        .then((data) => setPumpsArray(data.data.page_records.map(item => ({ title: item.name, query: `pump_names=${item.name}&` }))));
    }
  }, [toggleButton]);

  useEffect(() => {
    if (toggleButton === "block") {
      if (state.pump_names === "") {
        fetch(`${API_BASE}/station/nozzles`, {
          headers: { 'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token },
        })
          .then(response => response.json())
          .then(data => setNozzleArrayData(data.data.page_records.map(item => ({ title: tr("Nozzle Name: {0}, Nozzle Number: {1}", item.name, item.name), query: `nozzle_names=${item.name}&` }))));
      } else if (state.pump_names !== "") {
        fetch(`${API_BASE}/station/nozzles?` + state.pump_names.slice(0, -1), {
          headers: { 'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token },
        })
          .then(response => response.json())
          .then(data => setNozzleArrayData(data.data.page_records.map(item => ({ title: tr("Nozzle Name: {0}, Nozzle Number: {1}", item.name, item.name), query: `nozzle_names=${item.name}&` }))));
      }
    }
  }, [toggleButton, state.pump_names]);

  useEffect(() => {
    if (toggleButton === "block") {
      fetch(`${API_BASE}/station/tanks`, {
        headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token },
      })
        .then((response) => response.json())
        .then((data) => setFilterTankArrayData(data.data.page_records.map(item => ({ title: tr("Tank Name: {0}", item.name), query: `tank_names=${item.name}&` }))));
    }
  }, [toggleButton]);

  useEffect(() => {
    if (toggleButton === "block") {
      fetch(`${API_BASE}/station/products`, {
        headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token },
      })
        .then((response) => response.json())
        .then((data) => setFilterProductData(data.data.page_records.map(item => ({ title: tr("Product Name: {0}", item.name), query: `product_names=${item.name}&` }))));
    }
  }, [toggleButton]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <p><b>{tr("*Click on")} <i>{tr("Export")}</i> {tr("button in")} <i>{tr("Filters")}</i> {tr("to download Excel sheet.")}</b></p>

      {/*Filter input fields start here*/}
      <div className="FilterBar" style={{ display: toggleButton }}>
        <h3>{tr("Select filters")}</h3>

        <div>
          { !canFilterStations(token) ? (
            <></>
          ) : (
            <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              options={stationsArray}
              onChange={handleStationsChange}
              disableCloseOnSelect
              getOptionLabel={(option) => option.title}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.title}
                </li>
              )}
              style={{ width: 500 }}
              renderInput={(params) => (
                <TextField {...params} label={tr("Station")} placeholder={tr("Select Station")} />
              )}
            />
          )}
        </div>

        <div className="FilterSection">
          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={pumpsArray}
            onChange={handlePumpChange}
            onClick={initializeNozzleId}
            disableCloseOnSelect
            getOptionLabel={(option) => option.title}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.title}
              </li>
            )}
            style={{ width: 500 }}
            renderInput={(params) => (
              <TextField {...params} label={tr("Pumps")} placeholder={tr("Select Pump")} />
            )}
          />

          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={nozzleArrayData}
            onChange={handleNozzleChange}
            disableCloseOnSelect
            getOptionLabel={(option) => option.title}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.title}
              </li>
            )}
            style={{ width: 500 }}
            renderInput={(params) => (
              <TextField {...params} label={tr("Nozzles")} placeholder={tr("Select Nozzle")} />
            )}
          />
        </div>

        <div className="FilterSection">
          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={filterTankArrayData}
            onChange={handleTankChange}
            disableCloseOnSelect
            getOptionLabel={(option) => option.title}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.title}
              </li>
            )}
            style={{ width: 500 }}
            renderInput={(params) => (
              <TextField {...params} label={tr("Tanks")} placeholder={tr("Select Tank")} />
            )}
          />

          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={filterProductData}
            onChange={handleProductChange}
            disableCloseOnSelect
            getOptionLabel={(option) => option.title}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.title}
              </li>
            )}
            style={{ width: 500 }}
            renderInput={(params) => (
              <TextField {...params} label={tr("Products")} placeholder={tr("Select Product")} />
            )}
          />
        </div>

        <br />

        {/* ── Exact volume / amount ─────────────────────────────────────
            Matches the stored value exactly. See the note in sales.py: these
            columns are floating point, so a sale displayed as 10.00 may be
            stored as 9.9999999, and an exact match will not find it. */}
        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="volumes"
            type="number"
            onChange={handleChange}
            value={state.volumes}
            label={tr("Volume (L)")}
            id="outlined-margin-none-volumes"
            className={classes.textField}
            variant="outlined"
            inputProps={{ min: 0, step: "0.01" }}
          />
          <FormHelperText className="FormHelperText">
            {tr("Exact match")}
          </FormHelperText>
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="amounts"
            type="number"
            onChange={handleChange}
            value={state.amounts}
            label={tr("Amount")}
            id="outlined-margin-none-amounts"
            className={classes.textField}
            variant="outlined"
            inputProps={{ min: 0, step: "0.01" }}
          />
          <FormHelperText className="FormHelperText">
            {tr("Exact match")}
          </FormHelperText>
        </FormControl>

        <br />

        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="car_plates"
            onChange={handleChange}
            value={state.car_plates}
            label={tr("Car Plate")}
            id="outlined-margin-none-car-plates"
            className={classes.textField}
            variant="outlined"
          />
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="card_ids"
            onChange={handleChange}
            value={state.card_ids}
            label={tr("Card ID")}
            id="outlined-margin-none-card-ids"
            className={classes.textField}
            variant="outlined"
          />
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="tag_ids"
            onChange={handleChange}
            value={state.tag_ids}
            label={tr("Tag ID")}
            id="outlined-margin-none-tag-ids"
            className={classes.textField}
            variant="outlined"
          />
        </FormControl>
        <br />
        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="since_date"
            onChange={handleChange}
            value={state.since_date}
            id="date"
            label={tr("Since Date")}
            type="datetime-local"
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: "1" }}
          />
          <FormHelperText className="FormHelperText">
            {tr("Select Start Date")}
          </FormHelperText>
        </FormControl>

        <FormControl className={classes.formControl} noValidate>
          <TextField
            name="to_date"
            onChange={handleChange}
            value={state.to_date}
            id="date"
            label={tr("To Date")}
            type="datetime-local"
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: "1" }}
          />
          <FormHelperText className="FormHelperText">
            {tr("Select End Date")}
          </FormHelperText>
        </FormControl>

        <FormControl className={classes.FilterClearButtonClass} noValidate>
          <Button
            id="FilterClearButton"
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? tr("Exporting...") : tr("Export")}
          </Button>
        </FormControl>

        <FormControl className={classes.FilterClearButtonClass} noValidate>
          <Button
            id="FilterClearButton"
            variant="contained"
            color="primary"
            onClick={() => {
              // Clears the page cache too. Without this, re-applying the same
              // filters after a clear would serve rows from a cache entry
              // built under a different filter set.
              pageCacheRef.current = {};
              setState(emptyFilterState());
            }}
          >
            {tr("Clear Fields")}
          </Button>
        </FormControl>

        {isExporting && (
          <FormControl className={classes.FilterClearButtonClass} noValidate>
            <div style={{ color: '#1976d2', fontSize: '14px' }}>
              <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
              {tr("Preparing export... This may take a moment for large datasets.")}
            </div>
          </FormControl>
        )}

        <hr />
      </div>

      {/*Check boxes for viewing and hiding columns*/}
      <div className="CheckboxesBar" style={{ display: checkboxesView }}>
        <h3>{tr("Select columns to view and hide")}</h3>
        <FormGroup>
          <FormControlLabel control={<Checkbox checked={checkedSalesId} onChange={handleCheckboxChangeSalesId} inputProps={{ 'aria-label': 'Sales ID' }}/>} label={tr("Sales ID")} />
          <FormControlLabel control={<Checkbox checked={checkedReceiptId} onChange={handleCheckboxChangeReceiptId} inputProps={{ 'aria-label': 'Receipt ID' }}/>} label={tr("Receipt ID")} />
          <FormControlLabel control={<Checkbox checked={checkedStation} onChange={handleCheckboxChangeStation} inputProps={{ 'aria-label': 'Station' }}/>} label={tr("Station")} />
          <FormControlLabel control={<Checkbox checked={checkedNozzle} onChange={handleCheckboxChangeNozzle} inputProps={{ 'aria-label': 'Nozzle' }}/>} label={tr("Nozzle")} />
          <FormControlLabel control={<Checkbox checked={checkedFuel} onChange={handleCheckboxChangeFuel} inputProps={{ 'aria-label': 'Fuel' }}/>} label={tr("Fuel")} />
          <FormControlLabel control={<Checkbox checked={checkedTank} onChange={handleCheckboxChangeTank} inputProps={{ 'aria-label': 'Tank' }}/>} label={tr("Tank")} />
          <FormControlLabel control={<Checkbox checked={checkedAmount} onChange={handleCheckboxChangeAmount} inputProps={{ 'aria-label': 'Amount' }}/>} label={tr("Amount")} />
          <FormControlLabel control={<Checkbox checked={checkedPrice} onChange={handleCheckboxChangePrice} inputProps={{ 'aria-label': 'Unit Price' }}/>} label={tr("Unit Price")} />
          <FormControlLabel control={<Checkbox checked={checkedPlate} onChange={handleCheckboxChangePlate} inputProps={{ 'aria-label': 'Car Plate' }}/>} label={tr("Car Plate")} />
          <FormControlLabel control={<Checkbox checked={checkedTin} onChange={handleCheckboxChangeTin} inputProps={{ 'aria-label': 'TIN Number' }}/>} label={tr("TIN Number")} />
          <FormControlLabel control={<Checkbox checked={checkedCustomerName} onChange={handleCheckboxChangeCustomerName} inputProps={{ 'aria-label': 'Customer Name' }}/>} label={tr("Customer Name")} />
        </FormGroup>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 12 }}>
          <CircularProgress style={{ color: '#1976d2' }} />
          <span style={{ color: '#666', fontSize: 14 }}>{tr("Loading reports data...")}</span>
        </div>
      )}

      {/*Table render starts here*/}
      {!loading && (
        <ScrollX>
        <table
          id="reportsTable"
          className="table table-striped table-bordered table-hover"
        >
          <thead className="thead-dark">
            <tr>
              <th scope="col">#</th>
              <th hidden={!checkedSalesId} scope="col">{tr("Sales ID")}</th>
              <th hidden={!checkedReceiptId} scope="col">{tr("Receipt ID")}</th>
              <th hidden={!checkedStation} scope="col">{tr("Station")}</th>
              <th scope="col">{tr("Creation Date")}</th>
              <th scope="col">{tr("Pump")}</th>
              <th hidden={!checkedNozzle} scope="col">{tr("Nozzle")}</th>
              <th hidden={!checkedFuel} scope="col">{tr("Fuel")}</th>
              <th hidden={!checkedTank} scope="col">{tr("Tank")}</th>
              <th scope="col">{tr("Volume")}</th>
              <th hidden={!checkedAmount} scope="col">{tr("Amount")}</th>
              <th hidden={!checkedPrice} scope="col">{tr("Unit Price")}</th>
              <th hidden={!checkedPlate} scope="col">{tr("Car Plate")}</th>
              <th hidden={!checkedTin} scope="col">{tr("TIN Number")}</th>
              <th hidden={!checkedCustomerName} scope="col">{tr("Customer Name")}</th>
              <th scope="col">{tr("Receipt")}</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((item, index) => (
              <tr key={index}>
                <th scope="row">{(itemNo = itemNo + 1)}</th>
                <td hidden={!checkedSalesId}>{item.sales_id}</td>
                <td hidden={!checkedReceiptId}>{item.receipt_id}</td>
                <td hidden={!checkedStation}>{item.station_name}</td>
                <td>{item.creation_date}</td>
                <td>{item.pumps.name}</td>
                <td hidden={!checkedNozzle}>{item.nozzles.name}</td>
                <td hidden={!checkedFuel}>{item.products_info.name}</td>
                <td hidden={!checkedTank}>{item.tanks.name}</td>
                <td>{item.volume}</td>
                <td hidden={!checkedAmount}>{item.amount}</td>
                <td hidden={!checkedPrice}>{item.price}</td>
                <td hidden={!checkedPlate}>{item.license_plate}</td>
                <td hidden={!checkedTin}>{item.tin_number}</td>
                <td hidden={!checkedCustomerName}>{item.customer_name}</td>
                <td><button value={item.id} className="fas fa-receipt printButton" onClick={printReceipt} title={tr("Print receipt")}></button></td>
              </tr>
            ))}
            <tr>
              <th scope="col"></th>
              <th hidden={!checkedSalesId} scope="col"></th>
              <th hidden={!checkedReceiptId} scope="col"></th>
              <th hidden={!checkedStation} scope="col"></th>
              <th scope="col"></th>
              <th scope="col"></th>
              <th hidden={!checkedNozzle} scope="col"></th>
              <th hidden={!checkedFuel} scope="col"></th>
              <th hidden={!checkedTank} scope="col"></th>
              <th scope="col">{tr("Volume")}</th>
              <th hidden={!checkedAmount} scope="col">{tr("Amount")}</th>
              <th hidden={!checkedPrice} scope="col"></th>
              <th hidden={!checkedPlate} scope="col"></th>
              <th hidden={!checkedTin} scope="col"></th>
              <th hidden={!checkedCustomerName} scope="col"></th>
              <th scope="col"></th>
            </tr>
            <tr>
              <th scope="row">{tr("Total")}</th>
              <td></td>
              <td hidden={!checkedSalesId}></td>
              <td hidden={!checkedReceiptId}></td>
              <td hidden={!checkedStation}></td>
              <td></td>
              <td hidden={!checkedNozzle}></td>
              <td hidden={!checkedFuel}></td>
              <td hidden={!checkedTank}></td>
              <td>{(reportData.reduce(function (acc, reportData) {return acc + reportData.volume}, 0)).toFixed(2)}</td>
              <td hidden={!checkedAmount}>{(reportData.reduce(function (acc, reportData) {return acc + reportData.amount}, 0)).toFixed(2)}</td>
              <td hidden={!checkedPrice}></td>
              <td hidden={!checkedPlate}></td>
              <td hidden={!checkedTin}></td>
              <td hidden={!checkedCustomerName}></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        </ScrollX>
      )}

      {/*Pagination bar*/}
      <div className="ReportPagination">
        <Pagination
          count={currentPageState.num_pages}
          page={state.current_page}
          onChange={(event, newPage) => {
            setCurrentPage(event, newPage);
          }}
          variant="outlined"
          color="primary"
        />
      </div>
    </>
  );
}